import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircledIcon, CrossCircledIcon } from '@radix-ui/react-icons';
import axios from 'axios';
import { describeApiError } from './Notifications';

/**
 * Stripe sends redirect-based payment methods here. Before this route existed the
 * customer landed on a blank page and the credits were never granted.
 */
const PaymentComplete = ({ backendUrl, getAuthHeaders, onCreditsGranted }) => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [message, setMessage] = useState('');
  const confirmed = useRef(false);

  const paymentIntentId = searchParams.get('payment_intent');
  const redirectStatus = searchParams.get('redirect_status');

  useEffect(() => {
    if (confirmed.current) return;
    confirmed.current = true;

    if (!paymentIntentId) {
      setStatus('unknown');
      setMessage('We did not receive a payment reference from Stripe.');
      return;
    }

    if (redirectStatus === 'failed') {
      setStatus('failed');
      setMessage('Your bank declined the payment. You have not been charged.');
      return;
    }

    const confirm = async () => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.post(
          `${backendUrl}/api/confirm-payment`,
          { paymentIntentId },
          { headers }
        );
        setStatus('success');
        setMessage(
          response.data?.creditsAdded
            ? `${response.data.creditsAdded} credits have been added to your account.`
            : 'Your credits have been added to your account.'
        );
        if (onCreditsGranted) onCreditsGranted();
      } catch (error) {
        console.error('Error confirming payment after redirect:', error);
        const described = describeApiError(error, 'We could not confirm this payment automatically.');
        setStatus('needs-support');
        setMessage(described.message);
      }
    };

    confirm();
  }, [paymentIntentId, redirectStatus, backendUrl, getAuthHeaders, onCreditsGranted]);

  return (
    <div className="payment-complete">
      {status === 'checking' && (
        <>
          <span className="spinner payment-complete-spinner" />
          <h2>Confirming your payment...</h2>
          <p>This only takes a moment. Please do not close this tab.</p>
        </>
      )}

      {status === 'success' && (
        <>
          <CheckCircledIcon className="payment-complete-icon success" />
          <h2>Payment complete</h2>
          <p>{message}</p>
          <Link to="/" className="btn btn-primary">Back to Dashboard</Link>
        </>
      )}

      {(status === 'failed' || status === 'unknown') && (
        <>
          <CrossCircledIcon className="payment-complete-icon error" />
          <h2>Payment not completed</h2>
          <p>{message}</p>
          <Link to="/" className="btn btn-primary">Back to Dashboard</Link>
        </>
      )}

      {status === 'needs-support' && (
        <>
          <CrossCircledIcon className="payment-complete-icon error" />
          <h2>We could not confirm your payment</h2>
          <p>{message}</p>
          <p className="payment-complete-reference">
            If you were charged, contact support with this reference: <code>{paymentIntentId}</code>
          </p>
          <Link to="/" className="btn btn-primary">Back to Dashboard</Link>
        </>
      )}
    </div>
  );
};

export default PaymentComplete;
